type QuoteIndicators = {
  start: string;
  end: string;
};

type MarkdownRules = {
  removeLeadingSpaces?: boolean;
  contentHeadings: string[];
  sectionHeadings: string[];
  splitSectionHeadings?: boolean;
  listItemIndicators: RegExp[];
  quoteIndicators?: QuoteIndicators;
  findReplace: { [find: string]: string };
};

const cexMarkdownRules: { [id: string]: MarkdownRules } = {
  federal: {
    // eliminate leading text to avoid false code blocks, etc.
    removeLeadingSpaces: true,
    contentHeadings: [
      '\\d+[thrdnd]+ CONGRESS',
      ' *IN THE SENATE OF THE UNITED STATES',
      ' *A BILL',
    ],
    sectionHeadings: ['^ *(SECTION \\d+|SEC\\. \\d+)\\.'],
    // Lines starting with (1), (2), (3) etc should be list items
    listItemIndicators: [
      /^ *(\([0-9]+\))/gm,
      /^ *(\([A-Z]\))/gm,
      /^ *(\([xvi]+\))/gm,
      /^ *(\([XVI]+\))/gm,
    ],
    findReplace: {
      "(``|'')": '"',
    },
  },
  al: {
    contentHeadings: [],
    sectionHeadings: [],
    listItemIndicators: [],
    findReplace: {},
  },
  ca: {
    // these should each be H2
    contentHeadings: ['The people of the State of California do enact as follows:'],
    splitSectionHeadings: true,
    // this is ordered first item should be H3, second should be H4, etc.
    sectionHeadings: [
      '^(SECTION \\d+\\.|SEC. \\d+\\.)[ \\t]*(.*$)',
      '^((?:\\d+\\.)+)[ \\t]*(.*$)',
    ],
    // this is ordered, first item should have no indents, second should be indented once, etc.
    listItemIndicators: [
      /^(\([0-9]+\))/gm,
      /^(\([ABCDEFGH]\))/gm,
      //      /^(\([xvi]+\))/gm,
      /^(\([XVI]+\))/gm,
    ],
    findReplace: {
      '^(\\d+)\\.\\s': '$1 . ', // hack to prevent 4069. from becoming a list item. Could be better!
    },
  },
  ga: {
    contentHeadings: [
      // these should each be H2
      'a bill to be entitled',
      'an act',
      'BE IT ENACTED BY THE GENERAL ASSEMBLY OF GEORGIA',
    ],
    sectionHeadings: [
      '^([~=]+)? *PART (I|II|III|IV|V|VI|VII|VIII|IX|X) *$',
      '^([~=]+)?Article \\d+-?(\\d+)?',
      '^([~=]+)?(Section \\d+(-\\d+)?\\.(?![0-9])|Sec\\. \\d+\\.(?![0-9]))',
      '^([~=]+)?\\d+-\\d+-\\d+(\\.\\d+)?\\.$', // 46-3-200.2. (statutory references?)
    ],
    listItemIndicators: [
      /^("?([~=]+)?\([a-h]+\))/gm,
      /^("?([~=]+)?\([0-9.]+\)|\([a-z]+\)\([0-9]+\))/gm,
      /^("?([~=]+)?\([A-Z]+\))/gm,
      /^("?([~=]+)?\([ivx]+\))/gm,
    ],
    quoteIndicators: {
      start: '(\\n([~="]+)?"|\\n  \\* ")', // what about:  * "
      end: '"',
    },
    findReplace: {},
  },
  ia: {
    contentHeadings: [],
    sectionHeadings: [],
    listItemIndicators: [],
    findReplace: {},
  },
  il: {
    contentHeadings: [],
    sectionHeadings: [],
    listItemIndicators: [],
    findReplace: {},
  },
  mi: {
    contentHeadings: [],
    sectionHeadings: [],
    listItemIndicators: [],
    findReplace: {},
  },
  mn: {
    contentHeadings: ['a bill for an act', 'AN ACT', 'A RESOLUTION'],
    sectionHeadings: [
      '^Article \\d',
      '^(Section \\d+\\.(?![0-9])|Sec\\. \\d+\\.(?![0-9]))',
    ],
    listItemIndicators: [/^(\([0-9]+\))/gm],
    findReplace: {
      '^Subdivision [0-9]+\\.': '\n*$&* ',
      '^Subd\\. [0-9]+\\.': '\n*$&* ',
    },
  },
  la: {
    contentHeadings: [],
    sectionHeadings: [],
    listItemIndicators: [],
    findReplace: {},
  },
};

const DEFAULT_MARKDOWN_RULES: MarkdownRules = {
  contentHeadings: [],
  sectionHeadings: [],
  listItemIndicators: [],
  findReplace: {},
};

const formatMainHeadings = (text: string, contentHeadings: string[]) => {
  if (contentHeadings.length > 0) {
    // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
    const regex = new RegExp(
      `^(${contentHeadings.map((heading) => `${heading}.*`).join('|')})`,
      'gim',
    );
    return text.replace(regex, (match) => `## ${match}`);
  }
  return text;
};

const formatSectionHeadings = (
  initialText: string,
  sectionHeadings: string[],
  splitHeadings?: boolean,
) => {
  const markdownHeadings = ['###', '####', '#####', '######'];
  let text = initialText;
  sectionHeadings.forEach((heading, index) => {
    // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
    const regex = new RegExp(heading, 'gim');
    const level = Math.min(index, markdownHeadings.length);

    if (splitHeadings) {
      text = text.replace(regex, (match, p1, p2) => {
        return p2
          ? `\n${markdownHeadings[level]} ${p1}\n\n${p2}`
          : `\n${markdownHeadings[level]} ${match}`;
      });
    } else {
      text = text.replace(regex, (match) => `\n${markdownHeadings[level]} ${match}`);
    }
  });

  return text;
};

const formatListItems = (initialText: string, listItemIndicators: RegExp[]) => {
  const markdownListItems = ['  *', '    *', '      *', '        *'];
  let text = initialText;
  // Insert list item prefixes
  listItemIndicators.forEach((indicator, index) => {
    // const regex = new RegExp(indicator, 'g');
    const level = Math.min(index, markdownListItems.length);

    text = text.replace(indicator, `${markdownListItems[level]} $1`);
  });

  // Now, fix whitespace issues around lists
  // Parsing of lines within lists are sensitive to lines before and after them

  // IGNORING WHITESPACE LINES
  // if line A is a list item, register its level IF ABOVE ONE
  // if line B-Y is NOT a list item, record it
  // ... if line K is a list item ABOVE previous list level, reset list level
  // if line Z is a list item AT OR BELOW LEVEL OF LINE 1, space out recorded lines
  const newLines: string[] = [];
  let listLines: string[] = [];
  let lastListLevel = 0;
  let lastLineLevel = 0;
  const listLevelRegex = /^([ \t]+\*) /;
  text.split('\n').forEach((initLine, index) => {
    let line = initLine;
    const currentListLevel = listLevelRegex.test(line)
      ? listLevelRegex.exec(line)![1].length
      : 0;

    // If we encounter a section header, wipe away any previous list info
    if (line.substr(0, 1) === '#') {
      listLines = [];
      lastListLevel = 0;
    }

    if (
      lastListLevel > 3 &&
      currentListLevel === lastListLevel &&
      listLines.length > 0
    ) {
      // FIX
      listLines.forEach((fixLine, fixIndex) => {
        newLines[fixIndex] = `${' '.repeat(currentListLevel)}${fixLine}`;
      });
      listLines = []; // DUMP
    } else if (lastListLevel <= 3 && currentListLevel === lastListLevel) {
      listLines = []; // DUMP
    }
    if (line && line.trim() !== '' && currentListLevel === 0 && lastListLevel > 3) {
      // STORE: possible text that needs to be fixed
      listLines[index] = line;
    }
    if (
      line.trim() !== '' &&
      currentListLevel !== 0 &&
      currentListLevel !== lastListLevel
    ) {
      // SET
      lastListLevel = currentListLevel;
    }

    // TODO is this the best place to fix this?
    if (lastLineLevel === 0 && currentListLevel === 3) {
      // Fix first list needing another newline
      line = `\n${line}`;
    }
    lastLineLevel = currentListLevel;

    newLines[index] = line;
  });

  const fixedLines = newLines.join('\n');

  // Do some sanity checks
  // Find lists that we identified
  const findListRegex = /(\n)( +\*[\S\s]+?)(\n[^\s])/g;
  return fixedLines.replace(
    findListRegex,
    (match: string, p1: string, p2: string, p3: string) => {
      // give the list an extra line break at the start
      const fixedPre = `${p1}\n`;

      // if the list items are all lower-level, there may be too many indenting spaces
      // reset it to just two indent spaces if so
      const lowestNumOfSpaces = p2.split('\n').reduce((lowestSpaces, line) => {
        const spaceMatch = /^\s+/.exec(line);
        if (spaceMatch && spaceMatch[0].length < lowestSpaces) {
          return spaceMatch[0].length;
        }
        return lowestSpaces;
      }, 100);

      // OK, reset by that number of spaces if necessary
      const spacesToReduce = lowestNumOfSpaces - 2;
      // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
      const reduceRegex = new RegExp(`^\\s{${spacesToReduce}}`, 'mg');
      const fixedList = spacesToReduce > 0 ? p2.replace(reduceRegex, '') : p2;

      return `${fixedPre}${fixedList}${p3}`;
    },
  );
};

const formatFindReplace = (
  initialText: string,
  findReplace: { [find: string]: string },
) => {
  let text = initialText;
  const findReplaceMap = new Map(Object.entries(findReplace));
  findReplaceMap.forEach((replace, find) => {
    // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
    const regex = new RegExp(find, 'gim');
    text = text.replace(regex, replace);
  });

  return text;
};

const formatBlockQuotes = (text: string, quoteIndicators: QuoteIndicators) => {
  // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
  const findRegex = new RegExp(
    `${quoteIndicators.start}[\\s\\S]+?${quoteIndicators.end}`,
    'gi',
  );

  return text
    .replace(findRegex, (match) => match.replace(/\n/g, '\n> ')) // insert blockquotes
    .replace(/\n> "/g, '\n\n> "') // ensure two newlines before blockquote section
    .replace(/(\n> \n)([^>])/g, '\n\n$2'); // ensure no empty blockquote line at end
};

const performGeneralCleanup = (initialText: string, rules: MarkdownRules) => {
  let text = initialText;
  // remove spaces from any line that is whitespace-only to avoid insertion of an empty CODE block
  text = text.replace(/^[ \t]+$/gim, '');

  // remove any leading spaces from any line that doesn't start with *, 1., or >
  // this will kill code blocks - that's OK
  text = text.replace(/^( +)([^ *>0-9]|[0-9]+(?!\.))/gim, '$2');

  if (rules && rules.removeLeadingSpaces) {
    text = text.replace(/^[ \\]+/gim, '');
  }
  return text;
};

export const transformPlainToCEX = (initialText: string, stateAbbreviation: string) => {
  let text = initialText;
  let rules: MarkdownRules = stateAbbreviation
    ? cexMarkdownRules[stateAbbreviation]
    : cexMarkdownRules.federal;
  rules = rules || DEFAULT_MARKDOWN_RULES;
  text = performGeneralCleanup(text, rules);
  text = formatMainHeadings(text, rules.contentHeadings);
  text = formatSectionHeadings(text, rules.sectionHeadings, rules.splitSectionHeadings);
  text = formatListItems(text, rules.listItemIndicators);
  text = formatFindReplace(text, rules.findReplace);
  if (rules.quoteIndicators) {
    text = formatBlockQuotes(text, rules.quoteIndicators);
  }

  return text;
};

// Some hacks to cleanup flaws in markdown after diffing
// @todo might be worth fixing at root in diff utility
export const postDiffCleanup = (markdown: string) => {
  // Fix a list indicator "*" that is stuck on the inside of ins or del
  const changeTagOrderRegex = /^(\s*)(<ins>|<del>)(\*\s)/gim;
  let cleanedmarkdown = markdown.replace(changeTagOrderRegex, '$1$3$2');

  // A regular text line should not start with whitespace, because 4 spaces or more becomes a
  // "code block"
  const eliminateStartWhitespaceRegex = /^( +)([a-zA-Z<])/gim;
  cleanedmarkdown = cleanedmarkdown.replace(eliminateStartWhitespaceRegex, '$2');

  return cleanedmarkdown;
};
